import { FC, useEffect, useRef } from "react";
import { Popover, PopoverContent } from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@capacit-com/lara-law-shared-ui";
import { SynonymMatch } from "@/lib/synonymMatching";
import { ArrowRightLeft, ArrowRightToLine } from "lucide-react";

type Props = {
  matches: SynonymMatch[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (canonicalTerm: string) => void;
  anchorElement: HTMLElement | null;
  position?: { top: number; left: number };
  selectedIndex?: number;
};

const SynonymSuggestions: FC<Props> = ({
  matches,
  isOpen,
  onClose,
  onSelect,
  position,
  selectedIndex = -1,
}) => {
  const commandRef = useRef<HTMLDivElement>(null);
  const isMac = navigator.userAgent.toUpperCase().indexOf("MAC") >= 0;

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && commandRef.current) {
      const items = commandRef.current.querySelectorAll("[cmdk-item]");
      const selectedItem = items[selectedIndex] as HTMLElement;
      if (selectedItem) {
        selectedItem.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  if (!isOpen || matches.length === 0 || !position) {
    return null;
  }

  const handleSelect = (canonicalTerm: string) => {
    onSelect(canonicalTerm);
    onClose();
  };

  return (
    <Popover open={isOpen} onOpenChange={onClose}>
      <PopoverContent
        className="w-auto min-w-[200px] max-w-[min(300px,calc(100vw-1rem))] p-1"
        align="start"
        side="bottom"
        sideOffset={4}
        style={{
          position: "fixed",
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Prevent closing when clicking on textarea
          if (
            e.target instanceof HTMLElement &&
            e.target.tagName === "TEXTAREA"
          ) {
            e.preventDefault();
          }
        }}
      >
        <Command ref={commandRef} shouldFilter={false} value="">
          <CommandList className="max-h-[200px]">
            <CommandEmpty>Ingen forslag fundet</CommandEmpty>
            <CommandGroup heading="Synonymforslag" className="p-0">
              {matches.map((match, index) => (
                <CommandItem
                  key={`${match.groupId}-${match.canonicalTerm}-${index}`}
                  onSelect={() => handleSelect(match.canonicalTerm)}
                  className={`cursor-pointer px-2 py-1.5 text-sm gap-2 ${
                    selectedIndex === index
                      ? "bg-accent! text-accent-foreground!"
                      : "bg-transparent! data-[selected=true]:bg-transparent! data-[selected=true]:text-foreground!"
                  }`}
                  value={match.canonicalTerm}
                  data-selected={selectedIndex === index}
                >
                  <span className="font-medium min-w-[150px]">
                    {match.canonicalTerm}
                  </span>

                  {selectedIndex === index && (
                    <CommandShortcut className="flex flex-row items-center gap-1 text-accent-foreground!">
                      Tab
                      {isMac ? (
                        <ArrowRightToLine className="size-4 " />
                      ) : (
                        <ArrowRightLeft className="size-4 " />
                      )}
                    </CommandShortcut>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SynonymSuggestions;
